import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  styled,
  CircularProgress,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { HiOutlineBriefcase } from 'react-icons/hi';
import { RiPulseFill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import {
  ICandidateStageStatusPipelineSetting,
  ICandidate,
  updateJobCandidateNotesStateStage,
  getJobById,
  getCalendarAccounts,
  getOutlookCalendarAccounts,
  ICalendarAccount,
  uploadFileToS3,
  newMappingEntity,
  getJobCandidateInteraction,
  IDocument,
  IOutlookCalendarAccount,
  getSubmissionSettings,
  IJobFieldSettings,
  getCurrentUserTeams,
} from 'wisestep-api';
import {
  IUserDTO,
  WsButton,
  WsColors,
  WsDateTimePicker,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  formattedDateISO,
  Item,
  getCurrentZoneAbbr,
  getStandardTime,
  getDateUsingFormat,
  WsIconText,
  SubmissionTimeline,
  Spinner,
  WsSelect,
  WsSelectObj,
  WsDrawer,
  useWsEditor,
  Label,
  WsEditor,
  WsFileUpload,
  ControlledTextArea,
  WsDatePicker,
  extractDateFromISODate,
} from 'wisestep-base';
import { date, number, object, string } from 'yup';

import { useUserContext, useWorkspaceContext } from '../../context';
import ActionFooter from '../../features/conversationWidget/components/ActionFooter';
import CVViewModal from '../../features/conversationWidget/components/CVViewModal';
// import IResume from '../../features/conversationWidget/types/IResume';
import {
  ITrackCvPages,
  downloadCV,
  trackCV,
} from '../../features/conversationWidget/utils/cvHelper';
// import { updateResumeAndDocumentsSubmission } from '../../features/conversationWidget/utils/saveSubmission';
import { stageStatusPipelineType } from '../../features/crmSettings/customizeStageStatus/helper';
import { useStageStatusContext } from '../../features/submissions/context/StageStatusContext';
import useCandidateStatusesByStage from '../../hooks/useCandidateStatusesByStage';
import {
  getCandidateStageData,
  getStageStatusSettings,
  getCandidateStagesInWsSelectObj,
} from '../../utils/CandidateStageStatusHelper';
import { success, failed, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import CalendarEvent from '../CalendarEvent/CalendarEvent';
import StageStatusCalendarEvent from '../CalendarEvent/StageStatusCalendarEvent';

import { getFieldFromSelectedStatus } from './helper';
import OfferedPayRateField from './OfferedPayRateField';
import jobCandidateInteraction from './payload';

const workspaceIds: string[] = [];

const Meeting = styled(Grid)(() => ({
  border: `1px solid ${WsColors.TertiaryShades300}`,
  padding: '12px',
  cursor: 'pointer',
  color: WsColors.TheBlues500,
  borderRadius: '3px',
}));

export interface IOpenCvView {
  open: boolean;
  url: string | null;
}
export interface ICandidateStatuses {
  status: string;
  value: string;
}
export type UpdateStatusModalPropsType = {
  candidate: ICandidate;
  openStatusModal: boolean;
  onClose: () => void;
  onCVClick?: (args: number) => void;
  candidateIndex?: number;
  updateStageStatus?: (updatedStage: string, updatedStatus: string) => void;
  handleRefresh?: (id: string | null) => void;
  isUnifiedView?: boolean;
  jobFieldSettings?: IJobFieldSettings;
};
interface IUpdateStatusModal {
  stage: string;
  status: string;
  notes: string;
  offeredPayRate: any;
  joiningDate: string | null;
  stageDate?: string | null;
}
const mapHeaderSize = new Map();
mapHeaderSize.set('small', WsTypoVariants.BODY1);
mapHeaderSize.set('medium', WsTypoVariants.SUB_TITLE);
mapHeaderSize.set('large', WsTypoVariants.TITLE);

interface WsDrawerVariant {
  location: 'left' | 'right' | 'bottom';
  size: 'small' | 'compact' | 'medium' | 'large' | 'full';
}

const DrawerHeader = styled('div')(({ location }: WsDrawerVariant) => ({
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: location == 'right' ? 'flex-start' : 'flex-end',
  backgroundColor: WsColors.TertiaryShades100,
}));

export const allowedContractTypes = [
  'Contract',
  'Temporary',
  'Internship',
  'Freelance',
  'ContractToHireW2',
  'ContractW2',
  'ContractIndependent',
  'ContractToHireIndependent',
  'ContractCorpToCorp',
  'ContractToHireCorpToCorp',
  'PartTime',
  'FixedTermContract',
];

const UpdateStatusModal = ({
  candidate,
  openStatusModal,
  onClose,
  onCVClick,
  candidateIndex,
  updateStageStatus,
  handleRefresh,
  isUnifiedView = false,
  jobFieldSettings,
}: UpdateStatusModalPropsType) => {
  const navigate = useNavigate();

  const schema = object().shape({
    joiningDate: date().when([], {
      is: () => mandatoryFields.includes('Joining Date'),
      then: date().nullable(false).required('Please provide Joining date'),
      otherwise: date().nullable(true),
    }),
    notes: string().when([], {
      is: () => mandatoryFields.includes('Comments'),
      then: string().required('Please provide your comments for the stage status'),
    }),
    stageDate: date().when([], {
      is: () =>
        mandatoryFields.includes('Stage Date & Time') || mandatoryFields.includes('Stage Date'),
      then: date().nullable(false).required('Please provide Stage date'),
      otherwise: date().nullable(true),
    }),
    offeredPayRate: object().when([], {
      is: () => mandatoryFields.includes('Offer Payrate'),
      then: object()
        .shape({
          currency: string().required('Currency of candidate is required'),
          value: number()
            .required('Offered Salary/Payrate of candidate is required')
            .min(1, 'Offered Salary/Payrate of candidate is required'),
        })
        .required('Offered Salary/Payrate of candidate is required'),
    }),
  });

  const stageStatus = useStageStatusContext();
  const [stagesData, setStagesData] = useState<stageStatusPipelineType[]>([]);
  const defaultValues: IUpdateStatusModal = {
    stage: candidate?.applicationStage ?? '',
    status: candidate?.applicationState ?? '',
    notes: '',
    offeredPayRate: {
      currency:
        candidate?.offeredPayRateCurrency ??
        (jobFieldSettings && jobFieldSettings.defaultCurrency.selectedField
          ? jobFieldSettings.defaultCurrency.selectedField
          : ''),
      value: candidate.offeredPayRate ?? 0,
      timeSpan: candidate.offeredPayRateTimeSpan ?? candidate?.expectedBaseSalaryTimeSpan ?? null,
    },
    joiningDate: (candidate.joiningDate && extractDateFromISODate(candidate.joiningDate)) ?? null,
  };
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });
  const { handleSubmit, watch, setValue } = methods;
  const selectedStage = watch('stage');
  const selectedStatus = watch('status');

  const mandatoryFields = getFieldFromSelectedStatus(stagesData, selectedStage, selectedStatus);

  const message = "Sorry! You don't have privileges to update the status.";

  const loggedInUser = useUserContext().userDTO as IUserDTO;

  const workspace = useWorkspaceContext();

  const setNotification = useNotificationStore((state) => state.setNotification);

  const loggedInUserName = loggedInUser.firstName ?? loggedInUser.lastName ?? loggedInUser.email;

  const [updateAllowed, setUpdateAllowed] = useState(false);

  const [updateTrigger, setUpdateTrigger] = useState(false);

  const [stateLoaded, setStateLoaded] = useState(false);

  const [calendarEventAccount, setCalendarEventAccount] = useState<ICalendarAccount | null>(null);

  const [calendarEventAccountOutlook, setCalendarEventAccountOutlook] =
    useState<IOutlookCalendarAccount | null>(null);

  const [openEventCreation, setOpenEventCreation] = useState<boolean>(false);

  const [eventCheck, setEventCheck] = useState(false);

  const [openCalendarEvent, setOpenCalendarEvent] = useState(false);

  const [candidateStageStatusSettings, setCandidateStageStatusSettings] =
    useState<ICandidateStageStatusPipelineSetting>();

  const [stages, setStages] = useState<WsSelectObj[]>([]);
  const editor = useWsEditor('', (htmlBody) => methods.setValue('notes', htmlBody ?? ''));
  const [candidateInfo, setCandidate] = useState<ICandidate | null>(candidate);

  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [teamName, setTeamName] = useState<string>();
  // const [resume, setResume] = useState<IResume | null>(null);
  const [otherDocument, setOtherDocument] = useState<IDocument[]>([]);
  const ref = useRef<{ onSubmit: () => void }>();
  const [detail, setDetail] = useState<'TIMELINE' | 'CALENDAR' | null>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const response = await getStageStatusSettings();
      setStagesData(getCandidateStageData(response?.data));
    })();
  }, []);

  const checkEvent = async () => {
    try {
      const req = await getCalendarAccounts();
      const req_ = await getOutlookCalendarAccounts();
      setCalendarEventAccount(req.data[0] ?? null);
      setCalendarEventAccountOutlook(req_.data[0] ?? null);
      if (req.data[0] || req_.data[0]) {
        setDetail('CALENDAR');
        setOpenEventCreation(true);
      }
      setOpenCalendarEvent(true);
    } catch (e) {
      setNotification(failed('Some error occurred. Please try later'));
    }
    setEventCheck(true);
  };

  const closeCalender = () => {
    setOpenCalendarEvent(false);
    setDetail(null);
  };

  const [openCvView, setOpenCvView] = useState<IOpenCvView>({
    open: false,
    url: candidate.resumeLocation,
  });
  useEffect(() => {
    if (updateTrigger) {
      setUpdateTrigger(false);
    }
  }, []);
  useEffect(() => {
    (async () => {
      if (candidateInfo?.id) {
        const interactionRecord = await getJobCandidateInteraction(candidateInfo?.id);
        interactionRecord && setOtherDocument(interactionRecord.data.otherDocuments);
        candidate.otherDocuments = interactionRecord.data.otherDocuments;
      }
    })();
  }, [candidate, candidateInfo?.id]);

  const [jobType, setjobType] = useState('');

  const previousStageRef = useRef(selectedStage);

  useEffect(() => {
    if (
      candidateInfo?.applicationStage !== selectedStage ||
      candidateInfo?.applicationState !== selectedStatus
    ) {
      setValue('stageDate', null);
      setValue('joiningDate', null);
    }
  }, [candidateInfo, selectedStage, selectedStatus, setValue]);

  useEffect(() => {
    if (selectedStage !== previousStageRef.current) {
      setValue('status', '');
      previousStageRef.current = selectedStage;
    }
  }, [selectedStage, setValue]);

  useEffect(() => {
    (async () => {
      if (candidate.jobId) {
        let emails: string[] = loggedInUser?.email ? [loggedInUser.email] : [];
        if (Array.isArray(loggedInUser.oldEmails) && loggedInUser.oldEmails) {
          emails = [...emails, ...loggedInUser.oldEmails];
        }
        const jobReq = await getJobById(candidate.jobId);
        if (jobReq?.data?.jobType) {
          setjobType(jobReq?.data?.jobType);
        }
        const teamReq = await getCurrentUserTeams();
        const dataTeams = teamReq.data;
        const teamIds = [...dataTeams.ownsTeams, ...dataTeams.belongsToTeams];
        setTeamName(jobReq?.data?.team?.name);
        if (workspace.owns) {
          setUpdateAllowed(true);
        }
        if (jobReq?.data?.team?.id && teamIds.indexOf(jobReq.data.team.id) > -1) {
          setUpdateAllowed(true);
        }
        if (jobReq?.data?.createdBy && emails.indexOf(jobReq.data.createdBy) > -1) {
          setUpdateAllowed(true);
        }
        setCandidateStageStatusSettings(stageStatus);
        setStages(getCandidateStagesInWsSelectObj(stageStatus));
      }
      setStateLoaded(true);
    })();
  }, [
    candidate.jobId,
    loggedInUser.email,
    loggedInUser.oldEmails,
    stageStatus,
    workspace,
    workspace.owns,
  ]);

  const statuses = useCandidateStatusesByStage(
    candidateStageStatusSettings?.stageStatusPipeline ?? '',
    selectedStage
  );

  const statusesObjects: WsSelectObj[] = statuses?.map((status) => ({
    label: `${status.value}`,
    value: `${status.value}`,
  }));

  const onSubmit = async (data: FieldValues) => {
    setIsSubmit(true);
    if (
      mandatoryFields.includes('Offer Payrate') &&
      (data?.offeredPayRate?.value == null || data?.offeredPayRate?.value <= 0)
    ) {
      setNotification(warning('Please fill mandatory fields'));
      return;
    }
    if (!isEmpty(selectedStage) && isEmpty(selectedStatus)) {
      setNotification(warning('Please select candidate status'));
      return;
    }
    if (detail == undefined && mandatoryFields.includes('Events')) {
      setNotification(warning('Please fill mandatory fields'));
      return;
    }

    if (isEmpty(otherDocument) && mandatoryFields.includes('Other Documents')) {
      setNotification(warning('Please fill mandatory fields'));
      return;
    }
    setIsLoading(true);
    let editedNote = '';
    const endingNote = ` with Stage Date ${getDateUsingFormat(
      data.stageDate,
      'DD MMMM, YYYY, h:mm a'
    )} ${getStandardTime()} - UTC ${getCurrentZoneAbbr()}`;
    const notesByRecruiter = data.notes;
    jobCandidateInteraction.id = candidate.id ?? '';
    jobCandidateInteraction.applicationStage = selectedStage;
    jobCandidateInteraction.applicationState = selectedStatus;
    jobCandidateInteraction.stageDate = data.stageDate ?? new Date().toISOString();
    jobCandidateInteraction.joiningDate = data.joiningDate ?? null;
    jobCandidateInteraction.offeredPayRate = data.offeredPayRate.value ?? null;
    jobCandidateInteraction.offeredPayRateCurrency = data.offeredPayRate.currency ?? null;
    jobCandidateInteraction.offeredPayRateTimeSpan = data.offeredPayRate.timeSpan ?? null;

    if (
      (!isEmpty(candidate.applicationStage) && selectedStage !== candidate.applicationStage) ||
      (!isEmpty(candidate.applicationState) && selectedStatus !== candidate.applicationState)
    ) {
      editedNote = `The Candidate Status has now been updated from <strong> 
                    ${candidate.applicationStage}  </strong> and <strong> 
                    ${candidate.applicationState}  </strong> to <strong> 
                    ${selectedStage}  </strong> and <strong> 
                    ${selectedStatus}  </strong>`;
    } else {
      editedNote = `The Candidate Status is now  <strong> 
                    ${selectedStage}  </strong> and <strong> 
                    ${selectedStatus}`;
    }

    editedNote = `${editedNote}. ${!isEmpty(endingNote) ? endingNote : ''}`;

    if (!isEmpty(notesByRecruiter)) {
      editedNote = `${editedNote} <br/><br/> ${loggedInUserName} has added the following note : ${notesByRecruiter}`;
    }

    if (data.joiningDate && !isEmpty(String(data.joiningDate))) {
      editedNote = `${editedNote} <br/> Joining Date : ${getDateUsingFormat(
        data.joiningDate,
        'DD MMMM, YYYY'
      )}`;
    }
    jobCandidateInteraction.candidateNotes = [
      {
        notes: editedNote,
        createdBy: loggedInUserName ?? '',
        createdAt: formattedDateISO(new Date()),
      },
    ];
    jobCandidateInteraction.stageAndStateActions = [
      {
        stage: selectedStage,
        state: selectedStatus,
        id: '',
        updatedBy: loggedInUserName ?? '',
        updatedOn: formattedDateISO(new Date()),
      },
    ];
    jobCandidateInteraction.otherDocuments = otherDocument;
    try {
      const res = await updateJobCandidateNotesStateStage(jobCandidateInteraction);

      // let payload: ICandidate = res.data;
      // payload = {
      //   ...payload,
      //   offeredPayRate: data?.offeredPayRate?.value,
      //   offeredPayRateCurrency: data?.offeredPayRate?.value ? data?.offeredPayRate?.currency : null,
      //   offeredPayRateTimeSpan: data?.offeredPayRate?.value ? data?.offeredPayRate?.timeSpan : null,
      // };
      // payload.joiningDate = data.joiningDate ?? null;
      // await updateResumeAndDocumentsSubmission(payload, resume, otherDocument);
      candidate.candidateNotes = res?.data?.candidateNotes;
      setUpdateTrigger(true);
      setNotification(success('Stage Status updated'));
      handleRefresh && handleRefresh(res.data?.id);
      onClose();
      if (updateStageStatus) {
        updateStageStatus(selectedStage, selectedStatus);
      }
    } catch (e) {
      setDisableSubmit(false);
    }
    setIsLoading(false);
  };

  const onUpload = async (file: File[], field: string) => {
    const url = await uploadFileToS3(file[0]);
    if (field === 'document') {
      const alteredCandidate = (candidateInfo as ICandidate) ?? newMappingEntity();
      const alteredDocuments = [...otherDocument];
      alteredDocuments.push({
        documentLocation: url,
        originalName: file[0].name,
        tag: null,
        uploadedOn: new Date(),
      });
      setOtherDocument([...alteredDocuments]);
      alteredCandidate.otherDocuments = alteredDocuments;
      setCandidate({ ...alteredCandidate });
    } else if (field === 'resume') {
      const alteredCandidate: ICandidate = (candidateInfo as ICandidate) ?? newMappingEntity();
      const candidateResumes = alteredCandidate?.mappedCandidateResumes
        ? alteredCandidate?.mappedCandidateResumes
        : [];
      const alteredResumes = [...candidateResumes];
      alteredResumes.push({
        candidateMappingId: '',
        email: '',
        gitHubLink: '',
        id: '',
        linkedInLink: '',
        phone: '',
        pinCode: '',
        resumeFileOriginalName: file[0].name,
        resumeLocation: url,
        resumeUploadedOn: new Date(),
      });
      // setResume({
      //   resumeLocation: url,
      //   resumeFileOriginalName: file[0].name,
      //   resumeUploadedOn: new Date(),
      // });
      alteredCandidate.resumeLocation = url;
      alteredCandidate.resumeFileOriginalName = file[0].name;
      alteredCandidate.resumeUploadedOn = new Date();
      alteredCandidate.mappedCandidateResumes = alteredResumes;
      setCandidate({ ...alteredCandidate });
    }
  };
  const resumeAction = (action: string, url: string | null) => {
    switch (action) {
      case 'view':
        !isEmpty(url) && trackCV('view', url, ITrackCvPages.SUBMISSION);
        setOpenCvView({ open: true, url: url });
        break;
      case 'removeCandidate':
        if (candidateInfo) {
          const alteredCandidate = { ...candidateInfo };
          alteredCandidate.resumeFileOriginalName = '';
          alteredCandidate.resumeLocation = '';
          alteredCandidate.resumeUploadedOn = null;
          setCandidate({ ...alteredCandidate });
        }
        break;
      case 'removeOtherDocument':
        if (candidateInfo) {
          const alteredCandidate = { ...candidateInfo };
          alteredCandidate.otherDocuments = alteredCandidate?.otherDocuments?.filter(
            (document) => document.documentLocation != url
          );
          alteredCandidate.otherDocuments && setOtherDocument(alteredCandidate.otherDocuments);
          setCandidate({ ...alteredCandidate });
        }
        break;
    }
  };

  const getFieldSettings = async (isMapping) => {
    if (!isMapping) {
      const req = await getSubmissionSettings();
      return req.data;
    }
    return null;
  };

  const drawerSize = detail ? 'large' : 'small';

  const headerSize = 'large';

  const toggleTimeline = () => setDetail(detail === 'TIMELINE' ? null : 'TIMELINE');

  const isJobContract = allowedContractTypes.includes(jobType);

  const formLabel = (label: string, isRequired: boolean) => label + (isRequired ? '*' : '');

  return workspaceIds.includes(workspace.id as string) ? (
    <>
      {stateLoaded ? (
        <WsDrawer onClose={onClose} title={'Submissions'} open={openStatusModal} size="large">
          <Grid container p={2} bgcolor={WsColors.BorderGrey}>
            <Grid item xs={11} display="flex" alignItems={'center'}>
              {!openCalendarEvent ? (
                <Stack direction="row" spacing={1}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    {`Update Interview Status : ${candidate.mappedJobTitle}, ${candidate.jobCode}`}
                  </WsTypography>
                  <WsTypography
                    variant={WsTypoVariants.SUB_TITLE}
                    hoverText="Job Admin Team"
                  >{`(${teamName})`}</WsTypography>
                </Stack>
              ) : (
                <WsIconText
                  icon={<ArrowBackIcon />}
                  onClick={() => setOpenCalendarEvent(false)}
                  textSize={WsTypoVariants.SUB_TITLE}
                  text="Schedule a meeting"
                  textCursor="pointer"
                />
              )}
            </Grid>
          </Grid>
          {!openCalendarEvent ? (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container>
                  <Grid item xs={5} p={1}>
                    <Stack direction={'row'} spacing={2} p={2}>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                        {candidate.name}
                      </WsTypography>
                      <WsButton
                        variant="text"
                        startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                        onClick={
                          isUnifiedView
                            ? onClose
                            : () => onCVClick && candidateIndex != null && onCVClick(candidateIndex)
                        }
                      >
                        View Details
                      </WsButton>
                    </Stack>
                    {updateAllowed ? (
                      <Stack spacing={2} p={2}>
                        <WsTypography>What&apos;s the current status</WsTypography>
                        <WsSelect
                          name="stage"
                          selectArray={stages}
                          defaultValue={
                            candidate?.applicationStage ? candidate?.applicationStage : ''
                          }
                        />
                        <WsSelect
                          name="status"
                          selectArray={statusesObjects}
                          defaultValue={
                            candidate?.applicationState ? candidate?.applicationState : ''
                          }
                          required={!isEmpty(selectedStage)}
                        />
                        {mandatoryFields.includes('Stage Date & Time') ? (
                          <>
                            <Label>
                              {formLabel(
                                'Stage Date',
                                mandatoryFields.includes('Stage Date & Time')
                              )}
                            </Label>
                            <WsDateTimePicker
                              name="stageDate"
                              label="Stage Date"
                              disablePast={false}
                              popperProps={{ placement: 'right-end' }}
                            />
                          </>
                        ) : (
                          <>
                            <Label>
                              {formLabel('Stage Date', mandatoryFields.includes('Stage Date'))}
                            </Label>
                            <WsDatePicker name="stageDate" label="Stage Date" disablePast={false} />
                          </>
                        )}

                        <Grid container>
                          <Grid item xs={12}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                              CV
                            </WsTypography>
                            {candidateInfo?.resumeFileOriginalName ? (
                              <Stack direction="row" spacing={2} mb={2}>
                                <WsTypography variant={WsTypoVariants.BODY1}>
                                  {candidateInfo?.resumeFileOriginalName ?? ''}
                                </WsTypography>
                                <DownloadIcon
                                  onClick={() =>
                                    downloadCV(
                                      candidateInfo?.resumeLocation?.split('?')[0] ?? '',
                                      candidateInfo?.resumeFileOriginalName ?? ''
                                    )
                                  }
                                  sx={{
                                    color: WsColors.TheBlues500,
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                  }}
                                />
                                <VisibilityIcon
                                  onClick={() =>
                                    resumeAction &&
                                    resumeAction('view', candidateInfo.resumeLocation ?? '')
                                  }
                                  sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                                />
                                <CloseIcon
                                  onClick={() =>
                                    resumeAction &&
                                    resumeAction(
                                      'removeCandidate',
                                      candidateInfo.resumeLocation ?? ''
                                    )
                                  }
                                  sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                                />
                              </Stack>
                            ) : (
                              <WsFileUpload
                                multiple={false}
                                onUpload={(file: File[]) => onUpload && onUpload(file, 'resume')}
                                name="resume"
                                placeholderText=" "
                                bgRequired={false}
                                buttonText={'Upload CV'}
                                maxFileSize={3}
                              />
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                              {`Other Documents ${
                                mandatoryFields.includes('Other Documents') ? '*' : ''
                              }`}
                            </WsTypography>
                            <WsFileUpload
                              multiple={false}
                              onUpload={(file: File[]) => onUpload && onUpload(file, 'document')}
                              name={'document'}
                              placeholderText=" "
                              bgRequired={false}
                              buttonText={'Upload Other Files'}
                              maxFileSize={3}
                            />
                            {isSubmit &&
                              mandatoryFields.includes('Other Documents') &&
                              isEmpty(otherDocument) && (
                                <FormHelperText error>Please upload document</FormHelperText>
                              )}
                            {candidateInfo?.otherDocuments?.map((document, index: number) => (
                              <Stack key={index} direction="row" spacing={2} mb={2}>
                                <WsTypography variant={WsTypoVariants.BODY1}>
                                  {document?.originalName ?? ''}
                                </WsTypography>
                                <DownloadIcon
                                  onClick={() =>
                                    downloadCV(
                                      document?.documentLocation?.split('?')[0] ?? '',
                                      document?.originalName ?? ''
                                    )
                                  }
                                  sx={{
                                    color: WsColors.TheBlues500,
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                  }}
                                />
                                <VisibilityIcon
                                  onClick={() =>
                                    resumeAction &&
                                    resumeAction('view', document?.documentLocation ?? '')
                                  }
                                  sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                                />
                                <CloseIcon
                                  onClick={() =>
                                    resumeAction &&
                                    resumeAction(
                                      'removeOtherDocument',
                                      document?.documentLocation ?? ''
                                    )
                                  }
                                  sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                                />
                              </Stack>
                            ))}
                          </Grid>
                        </Grid>
                        <Meeting container onClick={checkEvent}>
                          <Grid item xs={9}>
                            Setup a Calendar meeting
                          </Grid>
                          <Grid item xs={3}>
                            <ChevronRightIcon sx={{ float: 'right' }} />
                          </Grid>
                        </Meeting>
                        {eventCheck && isEmpty(calendarEventAccount) && (
                          <FormHelperText error>No calendar account available</FormHelperText>
                        )}
                      </Stack>
                    ) : (
                      <Item>
                        <Grid container>
                          <Grid item xs={3}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>Stage</WsTypography>
                          </Grid>
                          <Grid item xs={9}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                              {selectedStage}
                            </WsTypography>
                          </Grid>
                          <Grid item xs={3} mt={2}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>Status</WsTypography>
                          </Grid>
                          <Grid item xs={9} mt={2}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                              {selectedStatus}
                            </WsTypography>
                          </Grid>
                          <Grid item xs={3} mt={2}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                              Stage Date
                            </WsTypography>
                          </Grid>
                          <Grid item xs={9} mt={2}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                              {candidate?.stageDate ?? ''}
                            </WsTypography>
                          </Grid>
                        </Grid>
                      </Item>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={7}
                    bgcolor={WsColors.TertiaryShades50}
                    sx={{ height: '500px', width: '800px', overflow: 'auto' }}
                  >
                    <Box m={2}>
                      <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SECONDARY}>
                        Previous Updates
                      </WsTypography>
                      <SubmissionTimeline
                        candidate={candidate}
                        getFieldSettings={getFieldSettings}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} p={3}>
                    <Label>Any Remarks would you like to add?</Label>
                    <Box minHeight={210} border={1} borderColor={'gray'}>
                      <WsEditor key="notes" editor={editor} resizable={true} />
                    </Box>
                  </Grid>
                  <Item sx={{ mb: 6 }}></Item>
                  <ActionFooter>
                    {!updateAllowed ? (
                      <Grid item xs={8} display="flex" justifyContent="center">
                        <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.ERROR}>
                          {message}
                        </WsTypography>
                      </Grid>
                    ) : (
                      <Grid item xs={8} display="flex" justifyContent="end">
                        <WsButton
                          variant="contained"
                          type="submit"
                          disabled={!updateAllowed && disableSubmit}
                        >
                          Update
                        </WsButton>
                      </Grid>
                    )}
                  </ActionFooter>
                </Grid>
              </form>
              {candidateInfo?.resumeLocation && (
                <CVViewModal
                  open={openCvView.open}
                  s3url={openCvView.url}
                  handleClose={() => setOpenCvView({ open: false, url: openCvView.url })}
                />
              )}
            </FormProvider>
          ) : openEventCreation ? (
            <CalendarEvent
              account={calendarEventAccount}
              oAccount={calendarEventAccountOutlook}
              candidate={candidate}
              onClose={closeCalender}
            />
          ) : (
            <Grid container>
              <Grid container item xs={12} justifySelf="flex-end" justifyContent="flex-end">
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Grid>
              <Grid mt={16} mb={2} container justifyContent="center">
                <Stack direction="column" spacing={2}>
                  <WsTypography>
                    No account is configured, Please configure your account to set up a meeting
                  </WsTypography>
                  <Grid item xs={12} display="flex" justifyContent="space-between" py={3} px={8}>
                    <WsButton onClick={() => navigate('/calender-settings/')}>
                      Configure gmail
                    </WsButton>
                    <WsButton onClick={() => navigate('/calender-settings/microsoft')}>
                      Configure outlook
                    </WsButton>
                  </Grid>
                </Stack>
              </Grid>
            </Grid>
          )}
        </WsDrawer>
      ) : (
        <Spinner />
      )}
    </>
  ) : (
    <>
      {stateLoaded ? (
        <WsDrawer
          onClose={onClose}
          title="Update Stage Status"
          open={openStatusModal}
          size={drawerSize}
          fullHeight
          hideHeader={detail === 'TIMELINE'}
        >
          {detail === 'TIMELINE' && (
            <DrawerHeader location="right" size={drawerSize}>
              <Stack direction="row" sx={{ flexGrow: 1 }}>
                <Stack direction="row" alignItems="center" sx={{ flex: '1 0 42%' }}>
                  <IconButton className="close-drawer" onClick={onClose}>
                    <CloseIcon />
                  </IconButton>
                  <WsTypography variant={mapHeaderSize.get(headerSize)}>
                    Update Stage Status
                  </WsTypography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ flex: '1 0 56%' }}
                >
                  <WsTypography variant={mapHeaderSize.get(headerSize)}>
                    Candidate Timeline
                  </WsTypography>
                  <IconButton className="close-drawer" onClick={toggleTimeline}>
                    <ChevronRightIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </DrawerHeader>
          )}
          <Stack direction="row" sx={{ flexGrow: 1 }}>
            <FormProvider {...methods}>
              <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ flex: '1 0 42%' }}>
                <Grid container>
                  <Grid item xs={12} p={1}>
                    <Stack direction={'row'} spacing={2} px={2}>
                      <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                        {candidate.name}
                      </WsTypography>
                      <WsButton
                        variant="text"
                        startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                        onClick={
                          isUnifiedView
                            ? onClose
                            : () => onCVClick && candidateIndex != null && onCVClick(candidateIndex)
                        }
                      >
                        View Details
                      </WsButton>
                      <WsButton
                        variant="text"
                        startIcon={<RiPulseFill color={WsColors.TheBlues500} />}
                        onClick={toggleTimeline}
                      >
                        Timeline
                      </WsButton>
                    </Stack>
                    <Stack px={3.5}>
                      <WsIconText
                        icon={<HiOutlineBriefcase />}
                        text={`${candidate.mappedJobTitle} ${candidate.jobCode}`}
                        opacity={true}
                        textCursor="pointer"
                      />
                    </Stack>
                    {updateAllowed ? (
                      <Stack spacing={2} p={2}>
                        <WsTypography>What&apos;s the current status</WsTypography>
                        <WsSelect
                          name="stage"
                          selectArray={stages}
                          defaultValue={
                            candidate?.applicationStage ? candidate?.applicationStage : ''
                          }
                        />
                        <WsSelect
                          name="status"
                          selectArray={statusesObjects}
                          defaultValue={
                            candidate?.applicationState ? candidate?.applicationState : ''
                          }
                          required={!isEmpty(selectedStage)}
                        />
                        {mandatoryFields.includes('Stage Date & Time') ? (
                          <>
                            <Label>
                              {formLabel(
                                'Stage Date',
                                mandatoryFields.includes('Stage Date & Time')
                              )}
                            </Label>
                            <WsDateTimePicker
                              name="stageDate"
                              label="Stage Date"
                              disablePast={false}
                              popperProps={{ placement: 'right-end' }}
                            />
                          </>
                        ) : (
                          <>
                            <Label>
                              {formLabel('Stage Date', mandatoryFields.includes('Stage Date'))}
                            </Label>
                            <WsDatePicker name="stageDate" label="Stage Date" disablePast={false} />
                          </>
                        )}
                        {mandatoryFields.includes('Offer Payrate') && (
                          <Grid>
                            <Label>
                              {(isJobContract ? 'Offered Pay Rate' : 'Offered Salary') +
                                (mandatoryFields.includes('Offer Payrate') ? ' *' : '')}
                            </Label>
                            <OfferedPayRateField name="offeredPayRate" />
                          </Grid>
                        )}
                        {mandatoryFields.includes('Joining Date') && (
                          <>
                            <Label>{formLabel('Joining Date', true)}</Label>
                            <WsDatePicker
                              name="joiningDate"
                              label="Joining Date"
                              disablePast={false}
                            />
                          </>
                        )}

                        <Button
                          size="medium"
                          variant="outlined"
                          onClick={() => setDetail('CALENDAR')}
                          sx={{ alignSelf: 'flex-end' }}
                        >
                          {'Create Event' + (mandatoryFields.includes('Events') ? '*' : '')}
                        </Button>
                        {isSubmit && mandatoryFields.includes('Events') && detail == undefined && (
                          <FormHelperText error sx={{ alignSelf: 'flex-end' }}>
                            Please add an Event
                          </FormHelperText>
                        )}
                        <Box>
                          <Label>
                            {formLabel('Comments', mandatoryFields.includes('Comments'))}
                          </Label>
                          <ControlledTextArea name="notes" minRows={5} style={{ width: '100%' }} />
                        </Box>
                        <Grid container>
                          <Grid item xs={12}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                              CV
                            </WsTypography>
                            {candidateInfo?.resumeFileOriginalName ? (
                              <Stack direction="row" spacing={2} mb={2}>
                                <WsTypography variant={WsTypoVariants.BODY1}>
                                  {candidateInfo?.resumeFileOriginalName ?? ''}
                                </WsTypography>
                                <DownloadIcon
                                  onClick={() =>
                                    downloadCV(
                                      candidateInfo?.resumeLocation?.split('?')[0] ?? '',
                                      candidateInfo?.resumeFileOriginalName ?? ''
                                    )
                                  }
                                  sx={{
                                    color: WsColors.TheBlues500,
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                  }}
                                />
                                <VisibilityIcon
                                  onClick={() =>
                                    resumeAction &&
                                    resumeAction('view', candidateInfo.resumeLocation ?? '')
                                  }
                                  sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                                />
                              </Stack>
                            ) : (
                              <WsFileUpload
                                multiple={false}
                                onUpload={(file: File[]) => onUpload && onUpload(file, 'resume')}
                                name="resume"
                                placeholderText=" "
                                bgRequired={false}
                                buttonText={'Upload CV'}
                                maxFileSize={3}
                              />
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
                              {`Other Documents ${
                                mandatoryFields.includes('Other Documents') ? '*' : ''
                              }`}
                            </WsTypography>
                            <WsFileUpload
                              multiple={false}
                              onUpload={(file: File[]) => onUpload && onUpload(file, 'document')}
                              name={'document'}
                              placeholderText=" "
                              bgRequired={false}
                              buttonText={'Upload Other Files'}
                              maxFileSize={3}
                            />
                            {isSubmit &&
                              mandatoryFields.includes('Other Documents') &&
                              isEmpty(otherDocument) && (
                                <FormHelperText error>Please upload document</FormHelperText>
                              )}
                            {candidateInfo?.otherDocuments?.map((document, index: number) => (
                              <Stack key={index} direction="row" spacing={2} mb={2}>
                                <WsTypography variant={WsTypoVariants.BODY1}>
                                  {document?.originalName ?? ''}
                                </WsTypography>
                                <DownloadIcon
                                  onClick={() =>
                                    downloadCV(
                                      document?.documentLocation?.split('?')[0] ?? '',
                                      document?.originalName ?? ''
                                    )
                                  }
                                  sx={{
                                    color: WsColors.TheBlues500,
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                  }}
                                />
                                <VisibilityIcon
                                  onClick={() =>
                                    resumeAction &&
                                    resumeAction('view', document?.documentLocation ?? '')
                                  }
                                  sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                                />
                                <CloseIcon
                                  onClick={() =>
                                    resumeAction &&
                                    resumeAction(
                                      'removeOtherDocument',
                                      document?.documentLocation ?? ''
                                    )
                                  }
                                  sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                                />
                              </Stack>
                            ))}
                          </Grid>
                        </Grid>
                        {eventCheck && isEmpty(calendarEventAccount) && (
                          <FormHelperText error>No calendar account available</FormHelperText>
                        )}
                      </Stack>
                    ) : (
                      <Item>
                        <Grid container>
                          <Grid item xs={3}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>Stage</WsTypography>
                          </Grid>
                          <Grid item xs={9}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                              {selectedStage}
                            </WsTypography>
                          </Grid>
                          <Grid item xs={3} mt={2}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>Status</WsTypography>
                          </Grid>
                          <Grid item xs={9} mt={2}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                              {selectedStatus}
                            </WsTypography>
                          </Grid>
                          <Grid item xs={3} mt={2}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                              Stage Date
                            </WsTypography>
                          </Grid>
                          <Grid item xs={9} mt={2}>
                            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                              {candidate?.stageDate ?? ''}
                            </WsTypography>
                          </Grid>
                        </Grid>
                      </Item>
                    )}
                  </Grid>
                  <Item sx={{ mb: 6 }}></Item>
                  {!updateAllowed ? (
                    <ActionFooter>
                      <Grid item xs={8} display="flex" justifyContent="center">
                        <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.ERROR}>
                          {message}
                        </WsTypography>
                      </Grid>
                    </ActionFooter>
                  ) : (
                    detail !== 'CALENDAR' && (
                      <ActionFooter>
                        <Grid item xs={8} display="flex" justifyContent="center">
                          <Button
                            sx={{ mr: 40 }}
                            variant="contained"
                            type="submit"
                            disabled={isLoading}
                            endIcon={
                              isLoading ? (
                                <CircularProgress size={17} color="secondary" />
                              ) : undefined
                            }
                          >
                            {isLoading ? 'Saving...' : 'Update'}
                          </Button>
                        </Grid>
                      </ActionFooter>
                    )
                  )}
                </Grid>
              </Box>
              {candidateInfo?.resumeLocation && (
                <CVViewModal
                  open={openCvView.open}
                  s3url={openCvView.url}
                  handleClose={() => setOpenCvView({ open: false, url: openCvView.url })}
                />
              )}
            </FormProvider>
            {detail === 'CALENDAR' && (
              <StageStatusCalendarEvent
                candidate={candidate}
                onClose={closeCalender}
                onSave={() => onSubmit(methods.getValues())}
                defaultValues={{ date: methods.getValues()['stageDate'] }}
                ref={ref}
              />
            )}
            {detail === 'TIMELINE' && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ backgroundColor: WsColors.TertiaryShades50 }}>
                  <SubmissionTimeline
                    candidate={candidate}
                    getFieldSettings={getFieldSettings}
                    hideTimelineHeader
                    withoutOppositeContent
                  />
                </Box>
              </>
            )}
          </Stack>
          {detail === 'CALENDAR' && (
            <Box
              sx={{
                background: WsColors.TertiaryShades50,
                position: 'sticky',
                bottom: '0',
                minWidth: '100%',
                p: 2.5,
              }}
            >
              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <WsButton variant="text" onClick={closeCalender}>
                  Cancel
                </WsButton>
                <WsButton
                  variant="contained"
                  type="submit"
                  onClick={() => ref.current?.onSubmit()}
                  disabled={isLoading}
                  endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : undefined}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </WsButton>
              </Stack>
            </Box>
          )}
        </WsDrawer>
      ) : (
        <Spinner />
      )}
    </>
  );
};

export default UpdateStatusModal;
